import {
    ReactElement,
    useCallback,
    useEffect,
    useState,
    FormEventHandler,
    useMemo,
    ReactNode
} from 'react';
import { CompanyUser } from '@/api';
import {
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UIMultiSelectInput,
    UISelectValue,
    UIText
} from '@/ui';
import { useUpdateTeamRoleAction, TeamRole } from '../actions';
import { Trans } from 'react-i18next';
import { useCompaniesTranslation } from '../hooks';

type UpdateTeamRoleModalProps = {
    companyUser: CompanyUser | null;
    teamId: string;
    onClose: () => void;
};

export function UpdateTeamRoleModal(
    props: UpdateTeamRoleModalProps
): ReactElement {
    const { companyUser, teamId, onClose } = props;

    return (
        <UIModal isOpen={companyUser !== null} onClose={onClose}>
            {companyUser && (
                <ModalContent
                    companyUser={companyUser}
                    teamId={teamId}
                    onClose={onClose}
                />
            )}
        </UIModal>
    );
}

/**
 * Mounted only once a member has been picked, so the action hook has a real
 * user to work from rather than the nullable one the list hands down.
 */
function ModalContent(props: {
    companyUser: CompanyUser;
    teamId: string;
    onClose: () => void;
}): ReactElement {
    const { companyUser, teamId, onClose } = props;

    const { updateTeamRole, loading } = useUpdateTeamRoleAction({
        user: companyUser,
        teamId
    });

    return (
        <UpdateTeamRoleModalForm
            teamId={teamId}
            companyUser={companyUser}
            onClose={onClose}
            loading={loading}
            onSubmit={({ roles }) => {
                updateTeamRole(roles)
                    .then(onClose)
                    .catch(() => {}); // Error handled in hook
            }}
        />
    );
}

type UpdateTeamRoleModalFormProps = {
    teamId: string;
    companyUser: CompanyUser;
    loading: boolean;
    onClose: () => void;
    onSubmit: (data: { roles: TeamRole[] }) => void;
};

function UpdateTeamRoleModalForm(
    props: UpdateTeamRoleModalFormProps
): ReactElement {
    const { teamId, companyUser, loading, onClose, onSubmit } = props;
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    const { t } = useCompaniesTranslation();

    /**
     * Team Roles
     */
    const admin: UISelectValue = useMemo(
        () => ({
            label: t('shared.roles.team.admin.name'),
            subLabel: t('shared.roles.team.admin.description'),
            value: 'admin'
        }),
        [t]
    );
    const guardian: UISelectValue = useMemo(
        () => ({
            label: t('shared.roles.team.guardian.name'),
            subLabel: t('shared.roles.team.guardian.description'),
            value: 'guardian'
        }),
        [t]
    );
    const memberOnly: UISelectValue = useMemo(
        () => ({
            label: t('shared.roles.team.member.name'),
            subLabel: t('shared.roles.team.member.description'),
            value: 'member'
        }),
        [t]
    );

    const teamRolesDefault: UISelectValue[] = useMemo(() => {
        const roles: UISelectValue[] = [];

        const teamUser = companyUser.teams.find(
            (teamUser) => teamUser.teamId === teamId
        );
        if (!teamUser) {
            return roles;
        }

        const isAdmin =
            teamUser.roles.filter(
                ({ name, inherited }) => name === 'admin' && !inherited
            ).length > 0;
        const isGuardian =
            teamUser.roles.filter(
                ({ name, inherited }) => name === 'guardian' && !inherited
            ).length > 0;

        if (isAdmin) {
            roles.push(admin);
        }
        if (isGuardian) {
            roles.push(guardian);
        }
        if (teamUser.roles.length === 0) {
            roles.push(memberOnly);
        }

        return roles;
    }, [admin, guardian, memberOnly, companyUser, teamId]);

    const [teamRoles, setTeamRoles] =
        useState<UISelectValue[]>(teamRolesDefault);
    const [teamRolesError, setTeamRolesError] = useState('');
    const teamRolesOptions: UISelectValue[] = useMemo(() => {
        // If roles contains member, only show member
        if (teamRoles.some((role) => role.value === 'member')) {
            return [memberOnly];
        } else if (teamRoles.length > 0) {
            return [admin, guardian];
        } else {
            return [admin, guardian, memberOnly];
        }
    }, [teamRoles, admin, guardian, memberOnly]);
    useEffect(() => {
        const memberRole = teamRoles.find((role) => role.value === 'member');
        if (memberRole && teamRoles.length > 1) {
            setTeamRoles([memberRole]);
        }
    }, [teamRoles]);

    const checkTeamRoles = useCallback(
        (selectedTeamRoles: UISelectValue[]): string => {
            if (selectedTeamRoles.length === 0) {
                return t('components.update_team_role_modal.error_required');
            }

            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setTeamRolesError(checkTeamRoles(teamRoles));
        }
    }, [teamRoles, showRealTimeErrors, checkTeamRoles]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkTeamRoles(teamRoles)) {
            setShowRealTimeErrors(false);

            const roles: ('admin' | 'guardian')[] = [];
            teamRoles.forEach((role) => {
                if (role.value === 'admin') {
                    roles.push('admin');
                }
                if (role.value === 'guardian') {
                    roles.push('guardian');
                }
            });

            onSubmit({
                roles
            });
        }
    };

    const inheritedRolesNote = useMemo(() => {
        const note: ReactNode[] = [];

        const teamUser = companyUser.teams.find(
            (teamUser) => teamUser.teamId === teamId
        );

        if (teamUser) {
            function capitalizeFirstLetter(val: string): string {
                return (
                    String(val).charAt(0).toUpperCase() + String(val).slice(1)
                );
            }

            function getTeamRoleName(role: string) {
                if (role === 'admin') {
                    return t('shared.roles.team.admin.short_name');
                } else if (role === 'guardian') {
                    return t('shared.roles.team.guardian.short_name');
                }
                return capitalizeFirstLetter(role);
            }

            teamUser.roles.forEach((role) => {
                if (role.inherited) {
                    if (role.inheritedFrom) {
                        note.push(
                            <Trans
                                ns="companies"
                                i18nKey="components.update_team_role_modal.note_inherited_from"
                                values={{
                                    companyRole: capitalizeFirstLetter(
                                        role.inheritedFrom
                                    ),
                                    teamRole: getTeamRoleName(role.name)
                                }}
                                components={{ b: <UIText bold /> }}
                            />
                        );
                    } else {
                        note.push(
                            <Trans
                                ns="companies"
                                i18nKey="components.update_team_role_modal.note_inherited"
                                values={{
                                    teamRole: getTeamRoleName(role.name)
                                }}
                                components={{ b: <UIText bold /> }}
                            />
                        );
                    }
                }
            });
        }

        return note;
    }, [companyUser, teamId, t]);

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UIModalHeader
                title={t('components.update_team_role_modal.title')}
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <UIMultiSelectInput
                        label={t('shared.fields.team_roles.label')}
                        placeholder={t('shared.labels.select')}
                        description={t(
                            'components.update_team_role_modal.roles_description'
                        )}
                        required
                        options={teamRolesOptions}
                        value={teamRoles}
                        onChange={setTeamRoles}
                        errorText={teamRolesError}
                    />

                    {inheritedRolesNote.map((note, index) => (
                        <UIText key={index}>{note}</UIText>
                    ))}
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: t('shared.labels.save')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </form>
    );
}
